export type ProgressMonthItem = {
  /**
   * Format: mm-yy (e.g. "08-25")
   */
  key: string;
  /**
   * Short label for charts (e.g. "Aug '25")
   */
  tickLabel: string;
  /**
   * Human-friendly label (e.g. "August 2025")
   */
  fullLabel: string;
};

const MONTH_NAMES_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"] as const;
const MONTH_NAMES_LONG = ["January","February","March","April","May","June","July","August","September","October","November","December"] as const;

export const pad2 = (n: number) => String(n).padStart(2, "0");

/**
 * Normalizes date strings to "mm-yy".
 * Accepts:
 * - "mm-yy" (current API)
 * - "yy-mm" (legacy)
 * - "yyyy-mm" (legacy)
 */
export const normalizeToMmYy = (value?: string): string | null => {
  if (!value) return null;
  const v = value.trim();
  const twoTwo = v.match(/^(\d{2})-(\d{2})$/);
  if (twoTwo) {
    const a = Number(twoTwo[1]);
    const b = Number(twoTwo[2]);
    // Current format: mm-yy (mm 01..12)
    if (a >= 1 && a <= 12) return `${pad2(a)}-${pad2(b)}`;
    // Legacy format: yy-mm (yy > 12, mm 01..12)
    if (a > 12 && b >= 1 && b <= 12) return `${pad2(b)}-${pad2(a)}`;
    return null;
  }

  const legacyYyyyMm = v.match(/^(\d{4})-(\d{2})$/);
  if (legacyYyyyMm) {
    const [, yyyy, mm] = legacyYyyyMm;
    const mmNum = Number(mm);
    if (mmNum < 1 || mmNum > 12) return null;
    return `${pad2(mmNum)}-${yyyy.slice(-2)}`;
  }

  return null;
};

/**
 * Parses a "mm-yy" string into a UTC Date at day 1.
 *
 * Note: Two-digit years are interpreted as 2000-2099.
 * If the input is invalid or cannot be normalized, returns an invalid Date (NaN).
 */
export const mmYyToDate = (mmYy: string) => {
  const normalized = normalizeToMmYy(mmYy);
  if (!normalized) return new Date(NaN);

  const [mmStr, yyStr] = normalized.split("-");
  const mm = Number(mmStr);
  const yy = Number(yyStr);
  if (!Number.isFinite(mm) || !Number.isFinite(yy)) return new Date(NaN);
  if (mm < 1 || mm > 12) return new Date(NaN);

  const year = 2000 + yy;
  return new Date(Date.UTC(year, mm - 1, 1));
};

export const toMmYy = (d: Date) =>
  `${pad2(d.getUTCMonth() + 1)}-${String(d.getUTCFullYear()).slice(-2)}`;

export const buildMonthRange = (startMmYy: string, endMmYy: string): ProgressMonthItem[] => {
  const start = mmYyToDate(startMmYy);
  const end = mmYyToDate(endMmYy);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];

  const months: ProgressMonthItem[] = [];
  const cur = new Date(start);
  // Safety guard: prevent infinite loops in case of unexpected Date mutation behavior.
  const MAX_ITERATIONS = 600; // 50 years of months is far beyond any expected UI range
  let iterations = 0;
  while (cur <= end && iterations < MAX_ITERATIONS) {
    const key = toMmYy(cur); // mm-yy
    const mIdx = cur.getUTCMonth();
    const fullYear = cur.getUTCFullYear();
    const tickLabel = `${MONTH_NAMES_SHORT[mIdx]} '${String(fullYear).slice(-2)}`;
    const fullLabel = `${MONTH_NAMES_LONG[mIdx]} ${fullYear}`;
    months.push({ key, tickLabel, fullLabel });
    cur.setUTCMonth(cur.getUTCMonth() + 1);
    iterations++;
  }
  return months;
};

/**
 * Returns an inclusive 6-month range ending on the current month, formatted as "mm-yy".
 * Example for Jan 2026: start="08-25", end="01-26".
 */
export const getDefaultSixMonthRangeMmYy = (now = new Date()) => {
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const start = new Date(end);
  start.setUTCMonth(start.getUTCMonth() - 5);
  return { startMmYy: toMmYy(start), endMmYy: toMmYy(end) };
};

export const addMonthsMmYy = (mmYy: string, deltaMonths: number) => {
  const d = mmYyToDate(mmYy);
  if (Number.isNaN(d.getTime())) return mmYy;
  d.setUTCMonth(d.getUTCMonth() + deltaMonths);
  return toMmYy(d);
};

export const shiftRangeMmYy = (
  range: { startMmYy: string; endMmYy: string },
  deltaMonths: number,
) => ({
  startMmYy: addMonthsMmYy(range.startMmYy, deltaMonths),
  endMmYy: addMonthsMmYy(range.endMmYy, deltaMonths),
});

/**
 * Returns the inclusive month span between two "mm-yy" values.
 * Example: "12-25" -> "01-26" === 2 (Dec, Jan)
 */
export const monthsDiffInclusiveMmYy = (startMmYy: string, endMmYy: string) => {
  const start = mmYyToDate(startMmYy);
  const end = mmYyToDate(endMmYy);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return NaN;
  const months = (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + (end.getUTCMonth() - start.getUTCMonth());
  return months + 1;
};
